import React from 'react';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import DashboardCard from './DashboardCard';

export interface LeadTemperatureChartProps {
  hot: number;
  warm: number;
  cold: number;
  loading?: boolean;
  className?: string;
}

/** Temperature colors come from theme CSS variables (no hardcoded hex in the component). */
const TEMP_COLORS = {
  hot: 'var(--temp-hot)',
  warm: 'var(--temp-warm)',
  cold: 'var(--temp-cold)',
} as const;

const LeadTemperatureChart: React.FC<LeadTemperatureChartProps> = ({ hot, warm, cold, loading, className }) => {
  const data = [
    { name: 'Hot', value: hot, color: TEMP_COLORS.hot },
    { name: 'Warm', value: warm, color: TEMP_COLORS.warm },
    { name: 'Cold', value: cold, color: TEMP_COLORS.cold },
  ];
  const total = hot + warm + cold;

  return (
    <DashboardCard
      title="Lead temperature"
      subtitle="Pipeline leads by priority (from your lead scores). Contact hot leads first."
      className={className}
    >
      {loading ? (
        <div className="flex h-44 items-center justify-center text-sm text-fg-muted">Loading…</div>
      ) : total === 0 ? (
        <div className="flex h-44 items-center justify-center rounded-md border border-dashed border-border text-sm text-fg-muted">
          No leads yet — mark temperature on leads to see them here.
        </div>
      ) : (
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 8 }}>
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={56}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--color-fg-muted)', fontSize: 13 }}
              />
              <Bar dataKey="value" radius={[4, 4, 4, 4]} barSize={24} isAnimationActive={false}>
                {data.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  style={{ fill: 'var(--color-fg)', fontSize: 13, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-fg-muted">
        {data.map((d) => (
          <span key={d.name} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
            {d.name} · <span className="font-semibold text-fg tabular-nums">{loading ? '…' : d.value}</span>
          </span>
        ))}
      </div>
    </DashboardCard>
  );
};

export default LeadTemperatureChart;
